/* istanbul ignore file */
import logger from '@adc/logger'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import instrument from '@adc/instrument'
import type { SavedSearch, Maybe, PartyPhone } from '@adc/sdk/types'
import {
  Button,
  useApplicationModals,
  useApplicationQueues,
  useApplicationNotifications,
  PhoneVerification,
  hooks as adcHooks,
} from '@adc/ui-adc'
import type { AuthenticatedAuctionUser } from '@adc/blueprint/lib/primitives'
import {
  ONE_CLICK_SAVE_SEARCH_START,
  ONE_CLICK_SAVE_SEARCH_SUCCESS,
  trackSaveSearch,
} from '../../../services/analytics'
import { saveSearch } from '../../../providers/search'
import { fetchUserDetails } from '../../../providers/user'
import { SaveSearchForm } from './savedSearchForm'
import {
  getModalVersionForAnalytics,
  getSavedSearchLookup,
  getSaveSearchButtonLabel,
  getSaveSearchParametersFromBlueprintData,
  SavedSearchModalProps,
} from './utils'

export const SaveSearchModal: React.FC<SavedSearchModalProps> = ({
  data,
  user,
}) => {
  const notifications = useApplicationNotifications()
  const queues = useApplicationQueues()
  const breakpoint = adcHooks.useBreakpoints()
  const smallButtons = breakpoint === 'tablet' || breakpoint === 'mobile'

  const modal = useApplicationModals()

  const [userSavedSearchData, setUserSavedSearchData] = useState(user)
  const [openedAt, setOpenedAt] = useState<number>()

  const [currentSavedSearch, setCurrentSavedSearch] =
    useState<Maybe<SavedSearch>>(null)

  const updateUserSavedSearchData = useCallback(
    (data: AuthenticatedAuctionUser) => setUserSavedSearchData(data),
    []
  )

  const modalProps = useMemo(() => {
    return {
      title: 'Search Saved!',
      size: 'small',
      children: (
        <SaveSearchForm
          filtersData={data}
          currentSavedSearch={currentSavedSearch}
          userSavedSearchData={userSavedSearchData as AuthenticatedAuctionUser}
          onModalClose={modal.close}
          updateUserSavedSearchData={updateUserSavedSearchData}
          openedAt={openedAt}
        />
      ),
      elmId: 'save_search',
    }
  }, [
    data,
    userSavedSearchData,
    currentSavedSearch,
    updateUserSavedSearchData,
    modal.close,
    openedAt,
  ])

  useEffect(() => {
    if (userSavedSearchData.logged_in && !data.isLoading) {
      setCurrentSavedSearch(
        getSavedSearchLookup(
          userSavedSearchData,
          data.listingCompatabilityFilters
        )
      )
    }
  }, [data.listingCompatabilityFilters, data.isLoading, userSavedSearchData])

  useEffect(() => {
    modal.update(modalProps)
  }, [modalProps, modal])

  useEffect(() => {
    setUserSavedSearchData(user)
  }, [user])

  const onSaveSearch = async () => {
    const [defaultSearchName, apiValue] =
      getSaveSearchParametersFromBlueprintData(data)

    const body = {
      parameters: apiValue,
      saved_search_name: defaultSearchName,
    }

    let newUserSavedSearchData = userSavedSearchData

    try {
      // In case of user who is not logged in, this onSaveSearch function is queued up in post auth queue
      // and when it finally runs after authentication, it doesn't have access to newly logged in user data.
      if (!userSavedSearchData.logged_in) {
        const user = (await fetchUserDetails()) as AuthenticatedAuctionUser
        newUserSavedSearchData = { ...user, logged_in: true }
      }

      const latestPhone = PhoneVerification.utils.getLatestPhone(
        newUserSavedSearchData.logged_in
          ? newUserSavedSearchData.party?.phones
          : []
      ) as Maybe<PartyPhone>

      const savedSearch = (await saveSearch(body)) as SavedSearch

      instrument.increment('search_save', 1)

      trackSaveSearch({
        event: ONE_CLICK_SAVE_SEARCH_SUCCESS as string,
        user: newUserSavedSearchData,
        savedSearchName: savedSearch.saved_search_name,
        smsNotificationsEnabled: !!savedSearch.sms_notifications_enabled,
        phoneVerified: !!latestPhone?.verified,
        phoneOptIn: !!latestPhone?.opt_in,
        modalVersion: getModalVersionForAnalytics(newUserSavedSearchData),
      })

      setCurrentSavedSearch(savedSearch)

      if (newUserSavedSearchData.logged_in) {
        setUserSavedSearchData({
          ...newUserSavedSearchData,
          saved_searches: [
            ...(newUserSavedSearchData.saved_searches ?? []),
            savedSearch,
          ],
        })
      }

      notifications.overlayBanner.open({
        title: 'Save Successful',
        message:
          'Your search criteria has been saved and we will notify you when new properties have been listed. Access all your notifications from your Dashboard.',
      })

      setOpenedAt(Date.now())
      modal.open('general', modalProps)
    } catch (err: unknown) {
      logger.error(err)
    }
  }

  const handleSaveSearch = async () => {
    const latestPhone = PhoneVerification.utils.getLatestPhone(
      userSavedSearchData.logged_in ? userSavedSearchData.party?.phones : []
    ) as Maybe<PartyPhone>

    const [defaultSearchName] = getSaveSearchParametersFromBlueprintData(data)

    const analyticsData: {
      event: string
      user: typeof userSavedSearchData
      savedSearchName: string
      smsNotificationsEnabled: false
      modalVersion: Maybe<string>
      phoneVerified?: boolean
      phoneOptIn?: boolean
    } = {
      event: ONE_CLICK_SAVE_SEARCH_START as string,
      user: userSavedSearchData,
      savedSearchName: defaultSearchName,
      smsNotificationsEnabled: false,
      modalVersion: getModalVersionForAnalytics(userSavedSearchData),
    }

    if (userSavedSearchData.logged_in) {
      analyticsData.phoneVerified = !!latestPhone?.verified
      analyticsData.phoneOptIn = !!latestPhone?.opt_in
    }

    trackSaveSearch(analyticsData)

    if (!user.logged_in) {
      queues.postOnboarding.push([onSaveSearch])
      // @ts-ignore modal types aren't correct
      modal.open('authentication')
    } else {
      await onSaveSearch()
    }
  }

  const extraButtonsStyle = { minWidth: smallButtons ? 48 : 130 }

  return (
    <Button
      disabled={currentSavedSearch !== null || data.isLoading}
      elmId='filters_save_search'
      onClick={handleSaveSearch}
      role='primary'
      style={extraButtonsStyle}
    >
      {getSaveSearchButtonLabel(Boolean(currentSavedSearch), smallButtons)}
    </Button>
  )
}
